import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plug, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "../ui/sonner";
import { EMULATOR } from "../../constants/testIds";

const PERSONA_LABELS = {
  super_admin: "Super Admin", front_of_house_mgr: "Manager", front_of_house_fd: "Front Desk",
  trainer: "Trainer", member: "Member", guest: "Guest",
};

const ESCALATE_TARGETS = [
  { key: "gym", label: "Gym Detail (seats/modules/license)" },
  { key: "integrations", label: "Integrations console" },
  { key: "monetization", label: "Monetization dashboard" },
];

function elapsedLabel(startedAt) {
  const ms = Date.now() - new Date(startedAt).getTime();
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// UX-CP-03 §1/§5 — the ONE persistent, non-dismissible flag that must render on every screen for
// the entire emulation session (mounted once at AppShell level + on the standalone "unavailable"
// page, never per-nav-item). No decorative motion beyond a single one-time entrance pulse
// (`emulation-flag-enter` in tokens.css) — a looping flag becomes wallpaper.
export function EmulationFlag({ emulation, orgId, gymName }) {
  const [now, setNow] = useState(Date.now());
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!emulation?.active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [emulation?.active]);

  if (!emulation?.active) return null;

  const exit = async () => {
    try {
      await api.post("/control/emulator/stop", { emulator_session_id: emulation.emulator_session_id });
      toast.success("Emulation session ended");
      navigate("/kairoo/dashboard");
    } catch (e) {
      toast.error(formatApiError(e).message);
    }
  };

  const escalate = async (target) => {
    try {
      await api.post("/control/emulator/stop", { emulator_session_id: emulation.emulator_session_id });
    } catch (e) {
      toast.error(formatApiError(e).message);
      return;
    }
    if (target === "gym") navigate(`/kairoo/gyms/${orgId || emulation.organization_id}`);
    if (target === "integrations") navigate("/kairoo/integrations");
    if (target === "monetization") navigate("/kairoo/monetization");
  };

  return (
    <>
      <div data-testid={EMULATOR.flagBar} className="emulation-flag emulation-flag-enter">
        <ShieldAlert size={18} className="shrink-0" />
        <div className="min-w-0 flex-1 truncate text-sm" data-testid={EMULATOR.flagPersonaGym}>
          Kairoo Support — Emulating <strong>{PERSONA_LABELS[emulation.persona] || emulation.persona}</strong> at{" "}
          <strong>{gymName || emulation.gym_name}</strong>
        </div>
        <div className="hidden sm:block text-xs opacity-90 shrink-0" data-testid={EMULATOR.flagOperator}>
          {emulation.operator_name}
        </div>
        <div className="text-sm tabular-nums-force shrink-0" data-testid={EMULATOR.flagTimer}>
          {elapsedLabel(emulation.started_at)}
        </div>
        <Button
          data-testid={EMULATOR.connectionsButton}
          size="sm" variant="secondary" className="shrink-0 gap-1.5"
          onClick={() => setConnectionsOpen(true)}
        >
          <Plug size={14} /> Connections
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={EMULATOR.escalateTrigger} size="sm" variant="secondary" className="shrink-0">
              Escalate
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {ESCALATE_TARGETS.map((t) => (
              <DropdownMenuItem key={t.key} data-testid={EMULATOR.escalateItem(t.key)} onClick={() => escalate(t.key)}>
                {t.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button data-testid={EMULATOR.exitButton} size="sm" variant="destructive" className="shrink-0 gap-1.5" onClick={exit}>
          <LogOut size={14} /> End session
        </Button>
      </div>
      <ConnectionsDialog
        open={connectionsOpen} onOpenChange={setConnectionsOpen}
        orgId={orgId || emulation.organization_id}
      />
    </>
  );
}

// SCR-P09-10 "integration status quick-view" — reachable from the wrapper without leaving
// emulation. Reuses the exact CP-02 endpoint (`GET /control/gyms/{orgId}/integrations`); the
// emulating ctx enforces the OPERATOR's own `secrets` permission server-side (see
// auth_deps.require_kairoo_permission), so an operator without it correctly sees an error here
// too, never a bypass.
function ConnectionsDialog({ open, onOpenChange, orgId }) {
  const [configs, setConfigs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !orgId) return;
    setConfigs(null);
    setError(null);
    api.get(`/control/gyms/${orgId}/integrations`)
      .then((res) => setConfigs(res.data.configs))
      .catch((e) => setError(formatApiError(e).message));
  }, [open, orgId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={EMULATOR.connectionsDialog} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Connections — quick diagnostic</DialogTitle>
        </DialogHeader>
        {error && <p className="text-sm" style={{ color: "var(--status-overdue-text)" }}>{error}</p>}
        {!error && !configs && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!error && configs && configs.length === 0 && (
          <p className="text-sm text-muted-foreground">This gym has no connectors configured.</p>
        )}
        {!error && configs && configs.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Channel</TableHead><TableHead>Provider</TableHead><TableHead>Status</TableHead><TableHead>Credential</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="capitalize">{c.channel}</TableCell>
                  <TableCell>{c.provider}</TableCell>
                  <TableCell className="capitalize">{c.status}</TableCell>
                  <TableCell>{c.secret.configured ? `•••• ${c.secret.secret_preview || ""}` : "Not set"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
